import { useState, useEffect } from 'react';
import { prescriptionAPI } from '../../lib/api';
import { 
  Pill, 
  ArrowClockwise,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Storefront,
  MagnifyingGlass,
  Funnel,
  CaretDown,
  CaretUp,
  Check,
  X
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function RefillManagementPage() {
  const [pendingRefills, setPendingRefills] = useState([]);
  const [allRefills, setAllRefills] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [showAll, setShowAll] = useState(false);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewData, setReviewData] = useState({ status: 'approved', denial_reason: '', notes: '' });

  useEffect(() => {
    loadData();
  }, [filterStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pendingRes, allRes, everyRes] = await Promise.all([
        prescriptionAPI.getPendingRefills(),
        prescriptionAPI.getAllRefills(filterStatus === 'all' ? null : filterStatus),
        prescriptionAPI.getAllRefills(null, 500)
      ]);
      setPendingRefills(pendingRes.data.requests || []);
      setAllRefills(allRes.data.requests || []);
      setStatusCounts((everyRes.data.requests || []).reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {}));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load refill requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!reviewModal) return;
    
    if (reviewData.status === 'denied' && !reviewData.denial_reason.trim()) {
      toast.error('Please provide a reason for denial');
      return;
    }
    
    setProcessing(reviewModal.id);
    try {
      await prescriptionAPI.reviewRefill(reviewModal.id, reviewData);
      toast.success(`Refill request ${reviewData.status}`);
      setReviewModal(null);
      setReviewData({ status: 'approved', denial_reason: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setProcessing(null);
    }
  };

  const handleQuickApprove = async (request) => {
    setProcessing(request.id);
    try {
      await prescriptionAPI.reviewRefill(request.id, { status: 'approved', notes: 'Quick approved' });
      toast.success('Refill approved');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleComplete = async (requestId) => {
    setProcessing(requestId);
    try {
      await prescriptionAPI.completeRefill(requestId);
      toast.success('Refill marked as completed');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1"><Clock size={12} /> Pending</span>;
      case 'approved':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1"><CheckCircle size={12} /> Approved</span>;
      case 'denied':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1"><XCircle size={12} /> Denied</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1"><CheckCircle size={12} /> Completed</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Refill Requests</h1>
        <p className="text-slate-500">Review and manage patient prescription refill requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{pendingRefills.length}</p>
              <p className="text-sm text-slate-500">Pending</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {statusCounts.approved || 0}
              </p>
              <p className="text-sm text-slate-500">Approved</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {statusCounts.denied || 0}
              </p>
              <p className="text-sm text-slate-500">Denied</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Pill size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {statusCounts.completed || 0}
              </p>
              <p className="text-sm text-slate-500">Completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Refills - Priority */}
      {pendingRefills.length > 0 && (
        <div className="card-clinical overflow-hidden border-2 border-amber-200">
          <div className="p-4 border-b border-slate-200 bg-amber-50">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Clock size={18} className="text-amber-600" />
              Pending Review ({pendingRefills.length})
            </h3>
          </div>

          <div className="divide-y divide-slate-100">
            {pendingRefills.map((request) => (
              <div key={request.id} className="p-4 hover:bg-slate-50" data-testid={`pending-refill-${request.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <User size={16} className="text-slate-400" />
                      <p className="font-medium text-slate-900">{request.patient_name}</p>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <Pill size={16} className="text-teal-500" />
                      <p className="font-semibold text-teal-700">{request.medication_name}</p>
                      <span className="text-slate-500">- {request.dosage}</span>
                    </div>
                    {request.pharmacy_name && (
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Storefront size={14} />
                        {request.pharmacy_name}
                      </p>
                    )}
                    {request.notes && (
                      <p className="text-sm text-slate-600 mt-1 italic">
                        Patient note: "{request.notes}"
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">
                      Requested: {new Date(request.requested_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleQuickApprove(request)}
                      disabled={processing === request.id}
                      className="px-3 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg flex items-center gap-1 disabled:opacity-50"
                      title="Quick approve"
                    >
                      {processing === request.id ? (
                        <div className="spinner w-4 h-4 border-green-600" />
                      ) : (
                        <Check size={18} />
                      )}
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        setReviewModal(request);
                        setReviewData({ status: 'denied', denial_reason: '', notes: '' });
                      }}
                      className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg flex items-center gap-1"
                      title="Deny with reason"
                    >
                      <X size={18} />
                      Deny
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="card-clinical p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Funnel size={18} className="text-slate-500" />
            <span className="text-slate-600">Filter:</span>
          </div>
          <div className="flex gap-2">
            {['pending', 'approved', 'denied', 'completed', 'all'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-teal-100 text-teal-700'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* All Refills List */}
      <div className="card-clinical overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-900">
            {filterStatus === 'all' ? 'All' : filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)} Refill Requests ({allRefills.length})
          </h3>
        </div>

        {allRefills.length === 0 ? (
          <div className="p-8 text-center">
            <ArrowClockwise size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No refill requests found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {allRefills.map((request) => (
              <div key={request.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-slate-900">{request.patient_name}</p>
                      {getStatusBadge(request.status)}
                    </div>
                    <p className="text-slate-700">{request.medication_name} - {request.dosage}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Requested: {new Date(request.requested_at).toLocaleDateString()}
                      {request.reviewed_at && ` | Reviewed: ${new Date(request.reviewed_at).toLocaleDateString()}`}
                    </p>
                    {request.denial_reason && (
                      <p className="text-sm text-red-600 mt-1">Reason: {request.denial_reason}</p>
                    )}
                  </div>
                  {request.status === 'approved' && (
                    <button
                      onClick={() => handleComplete(request.id)}
                      disabled={processing === request.id}
                      className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm disabled:opacity-50"
                    >
                      {processing === request.id ? 'Processing...' : 'Mark Completed'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900">
                {reviewData.status === 'approved' ? 'Approve' : 'Deny'} Refill Request
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <p className="text-slate-600">Patient: <strong>{reviewModal.patient_name}</strong></p>
                <p className="text-slate-600">Medication: <strong>{reviewModal.medication_name}</strong></p>
              </div>

              {reviewData.status === 'denied' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Reason for Denial <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={reviewData.denial_reason}
                    onChange={(e) => setReviewData({ ...reviewData, denial_reason: e.target.value })}
                    placeholder="Please provide a reason for the patient..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    rows={3}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optional)</label>
                <textarea
                  value={reviewData.notes}
                  onChange={(e) => setReviewData({ ...reviewData, notes: e.target.value })}
                  placeholder="Internal notes..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  rows={2}
                />
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setReviewModal(null);
                  setReviewData({ status: 'approved', denial_reason: '', notes: '' });
                }}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleReview}
                disabled={processing === reviewModal.id}
                className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${
                  reviewData.status === 'approved' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {processing === reviewModal.id ? 'Processing...' : `Confirm ${reviewData.status === 'approved' ? 'Approval' : 'Denial'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
